"use client"
import { shopifyImg } from "@/lib/util/img"

import { usePathname, useRouter } from "next/navigation"
import { Fragment, useEffect, useId, useRef, useState } from "react"

type Price = { rrp: number | null; hasGst: boolean; multi: boolean; trade?: number | null; margin?: number | null }
type Hit = { id: string; handle: string; title: string; brand: string; thumbnail: string | null; variant_count: number; price?: Price | null }
type Trade = { status: "guest" | "pending" | "approved"; tier?: string }
type Suggestion = { name: string; handle: string; count: number; match?: boolean }
type Result = { q: string; hits: Hit[]; total: number; relaxed: boolean; brands: Suggestion[]; categories: Suggestion[]; trade: Trade }

const aud = (n: number) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n)

// Same rules as TradePrice on the cards: approved = trade price ex GST + RRP + margin; guests/pending = RRP only
function PriceLine({ p, trade }: { p?: Price | null; trade: Trade }) {
  if (!p) return null
  if (trade.status === "approved" && typeof p.trade === "number") {
    return (
      <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs text-elevate-grey-mid" data-testid="search-trade-price">
        <span className="text-sm font-medium text-elevate-black">{p.multi ? "From " : ""}{aud(p.trade)}</span>
        <span>ex GST</span>
        {p.rrp !== null && <span>RRP {aud(p.rrp)}</span>}
        {typeof p.margin === "number" && <span className="font-medium text-emerald-800">{Math.round(p.margin * 100)}% margin</span>}
      </span>
    )
  }
  return (
    <span className="mt-0.5 block text-xs text-elevate-grey-mid" data-testid="search-rrp-price">
      {p.rrp !== null ? <span className="text-elevate-black">RRP {aud(p.rrp)}</span> : "Trade product"}
      {trade.status === "pending" ? " · trade price once approved" : trade.status === "guest" ? " · log in for trade price" : ""}
    </span>
  )
}

const thumb = (url: string | null) => shopifyImg(url, 128) ?? null
const RECENT_KEY = "elevate_recent_searches"

function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]")
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, 6) : []
  } catch {
    return []
  }
}
function saveRecent(q: string) {
  try {
    const t = q.trim()
    if (!t) return
    localStorage.setItem(RECENT_KEY, JSON.stringify([t, ...readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 6)))
  } catch {}
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
function Highlight({ text, q }: { text: string; q: string }) {
  const words = q.trim().split(/\s+/).filter((w) => w.length > 1).map(escapeRe)
  if (!words.length) return <>{text}</>
  const parts = text.split(new RegExp(`(${words.join("|")})`, "ig"))
  return <>{parts.map((p, i) => (i % 2 === 1 ? <mark key={i} className="bg-transparent font-semibold text-elevate-black">{p}</mark> : <Fragment key={i}>{p}</Fragment>))}</>
}

/**
 * Header search with instant, typo-tolerant suggestions (Meilisearch via /api/search).
 * 3 Oct 2026 (BOO parity, Jayson: "1-liners ... should be an enriched drop down"): wide panel with brand + category
 * short links on the left, a 2-column product grid on the right, "Shop all <brand>" when the text names a brand,
 * recent searches on focus. Keyboard: arrows walk every link in DOM order, Enter opens it, Escape closes.
 */
const HeaderSearch = ({ className = "", autoFocus = false, onSubmitted }: { className?: string; autoFocus?: boolean; onSubmitted?: () => void }) => {
  const router = useRouter()
  // ?q= is read after mount, not with useSearchParams(): that made this component suspend during SSR, so it streamed
  // into a no-fallback Suspense boundary that React 19.2 (canary in Next 15.5) reveals in a throttled batch -- when
  // hydration got there first it threw #418 (~2% of loads, caught by the client error reporter 25 Sep 2026).
  const pathname = usePathname()
  const [q, setQ] = useState("")
  useEffect(() => {
    setQ(new URLSearchParams(window.location.search).get("q") ?? "")
  }, [pathname])
  const [result, setResult] = useState<Result | null>(null)
  const [loading, setLoading] = useState(false)
  const [recent, setRecent] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const listId = useId()
  const boxRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setResult(null); setLoading(false); return }
    const ctl = new AbortController()
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/search?q=${encodeURIComponent(term)}`, { signal: ctl.signal })
        const j = await r.json()
        setResult({ q: term, hits: j.hits ?? [], total: j.total ?? 0, relaxed: !!j.relaxed, brands: j.suggestions?.brands ?? [], categories: j.suggestions?.categories ?? [], trade: j.trade ?? { status: "guest" } })
        setActive(-1)
      } catch {
      } finally {
        if (!ctl.signal.aborted) setLoading(false)
      }
    }, 120)
    return () => { clearTimeout(t); ctl.abort() }
  }, [q])

  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("touchstart", onDown)
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown) }
  }, [])

  const go = (href: string, term?: string) => { if (term) saveRecent(term); setOpen(false); router.push(href); onSubmitted?.() }
  const links = () => Array.from(panelRef.current?.querySelectorAll<HTMLAnchorElement>("[data-nav]") ?? [])
  const submit = () => {
    const term = q.trim()
    const el = active >= 0 ? links()[active] : undefined
    if (el) return go(el.getAttribute("href")!, term)
    go(term ? `/store?q=${encodeURIComponent(term)}` : `/store`, term)
  }

  const showList = open && q.trim().length >= 2 && !!result
  const showRecent = open && !q.trim() && recent.length > 0
  const term = result?.q ?? q.trim()
  const matchedBrand = result?.brands.find((b) => b.match)

  // flat keyboard index across banner, brands, categories, products (DOM order)
  let nav = -1
  const navProps = (href: string) => {
    nav += 1
    const idx = nav
    return {
      href,
      "data-nav": "",
      id: `${listId}-opt-${idx}`,
      role: "option",
      "aria-selected": active === idx,
      onMouseEnter: () => setActive(idx),
      onClick: (e: React.MouseEvent) => { e.preventDefault(); go(href, term) },
    }
  }
  const linkCls = "block rounded-full border border-elevate-border px-3 py-1 text-sm hover:border-elevate-black aria-selected:bg-elevate-cream medium:rounded medium:border-0 medium:px-2 medium:py-1.5 medium:hover:bg-elevate-cream"
  const heading = "mb-2 text-[11px] uppercase tracking-[0.12em] text-elevate-grey-mid"

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <form role="search" onSubmit={(e) => { e.preventDefault(); submit() }}>
        <label htmlFor={`${listId}-input`} className="sr-only">Search products</label>
        <input
          id={`${listId}-input`}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={`${listId}-list`}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${listId}-opt-${active}` : undefined}
          value={q}
          autoFocus={autoFocus}
          autoComplete="off"
          onFocus={() => { setRecent(readRecent()); setOpen(true) }}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
          onKeyDown={(e) => {
            if (e.key === "Escape") return setOpen(false)
            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return
            const all = links()
            if (!all.length) return
            e.preventDefault()
            setOpen(true)
            const n = all.length
            const next = e.key === "ArrowDown" ? (active + 1) % n : (active - 1 + n) % n
            setActive(next)
            all[next].scrollIntoView({ block: "nearest" })
          }}
          placeholder="Search products, brands, SKUs…"
          className="w-full bg-elevate-grey-light text-elevate-black text-sm pl-4 pr-10 py-2 rounded-full border border-transparent focus:border-elevate-black focus:outline-none"
        />
        <button type="submit" aria-label="Search" className="absolute right-1 top-[19px] -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-full hover:bg-white">
          {loading ? (
            <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-elevate-border border-t-elevate-black" />
          ) : (
            <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          )}
        </button>
      </form>

      {showRecent && (
        <div className="absolute right-0 mt-2 w-full medium:w-[min(92vw,420px)] bg-white border border-elevate-border rounded-image shadow-[0_12px_32px_-12px_rgba(0,0,0,0.25)] z-[60] p-4">
          <p className={heading}>Recent searches</p>
          <div className="flex flex-wrap gap-2">
            {recent.map((r) => (
              <button key={r} type="button" onClick={() => setQ(r)} className="rounded-full border border-elevate-border px-3 py-1 text-sm hover:border-elevate-black">{r}</button>
            ))}
          </div>
        </div>
      )}

      {showList && result && (
        <div
          ref={panelRef}
          id={`${listId}-list`}
          role="listbox"
          aria-label="Suggestions"
          className="absolute right-0 mt-2 w-full medium:w-[min(calc(100vw-32px),900px)] max-h-[calc(100vh-120px)] overflow-y-auto bg-white border border-elevate-border rounded-image shadow-[0_12px_32px_-12px_rgba(0,0,0,0.25)] z-[60]"
          data-testid="header-search-results"
        >
          {matchedBrand && (
            <a {...navProps(`/categories/${matchedBrand.handle}`)} className="flex items-center justify-between gap-3 border-b border-elevate-border bg-elevate-cream px-4 py-3 text-sm hover:bg-elevate-sage aria-selected:bg-elevate-sage">
              <span>Shop all <span className="font-semibold">{matchedBrand.name}</span></span>
              <span className="text-xs uppercase tracking-[0.1em]">View brand →</span>
            </a>
          )}
          <div className="flex flex-col medium:flex-row">
            {(result.brands.length > 0 || result.categories.length > 0) && (
              <aside className="border-b border-elevate-border px-4 py-4 medium:w-56 medium:shrink-0 medium:border-b-0 medium:border-r">
                {result.brands.length > 0 && (
                  <div className="mb-4">
                    <p className={heading}>Brands</p>
                    <ul className="flex flex-wrap gap-2 medium:block medium:space-y-0.5">
                      {result.brands.map((b) => (
                        <li key={b.handle}><a {...navProps(`/categories/${b.handle}`)} className={linkCls}><Highlight text={b.name} q={term} /></a></li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.categories.length > 0 && (
                  <div>
                    <p className={heading}>Categories</p>
                    <ul className="flex flex-wrap gap-2 medium:block medium:space-y-0.5">
                      {result.categories.map((c) => (
                        <li key={c.handle}><a {...navProps(`/categories/${c.handle}`)} className={linkCls}>{c.name}</a></li>
                      ))}
                    </ul>
                  </div>
                )}
              </aside>
            )}
            <section className="min-w-0 flex-1 p-3">
              <div className="flex items-baseline justify-between px-2 pb-2">
                <p className={heading + " mb-0"}>{result.relaxed ? "Close matches" : "Products"}</p>
                {result.total > 0 && <p className="text-xs text-elevate-grey-mid">{result.total} {result.total === 1 ? "product" : "products"}</p>}
              </div>
              {result.hits.length === 0 ? (
                <p className="px-2 py-6 text-sm text-elevate-grey-mid">No matches for “{term}”.{matchedBrand ? " See the brand above." : " Try a brand or a shorter search."}</p>
              ) : (
                <ul className="grid grid-cols-1 gap-1 medium:grid-cols-2">
                  {result.hits.map((h) => (
                    <li key={h.id}>
                      <a {...navProps(`/products/${h.handle}`)} className="flex items-center gap-3 rounded px-2 py-2 hover:bg-elevate-cream aria-selected:bg-elevate-cream">
                        <span className="h-16 w-16 shrink-0 rounded bg-elevate-grey-light overflow-hidden">
                          {h.thumbnail && <img src={thumb(h.thumbnail) ?? undefined} alt="" width={64} height={64} loading="lazy" className="h-full w-full object-contain" />}
                        </span>
                        <span className="min-w-0">
                          {h.brand && <span className="block truncate text-[11px] uppercase tracking-[0.1em] text-elevate-grey-mid">{h.brand}</span>}
                          <span className="block text-sm leading-snug line-clamp-2"><Highlight text={h.title} q={term} /></span>
                          {h.variant_count > 1 && <span className="block text-xs text-elevate-grey-mid">{h.variant_count} sizes</span>}
                          <PriceLine p={h.price} trade={result.trade} />
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
          {result.total > 0 && (
            <button type="button" onClick={() => go(`/store?q=${encodeURIComponent(term)}`, term)} className="w-full border-t border-elevate-border px-4 py-3 text-left text-xs uppercase tracking-[0.1em] hover:bg-elevate-grey-light">
              See all {result.total} results for “{term}” →
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default HeaderSearch
